import { Toaster } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, Suspense, useEffect } from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import { BrowserRouter, Route, Routes, useNavigate } from "react-router-dom";
import "./App.scss";
import { TopMenu } from "./components/TopMenu";
import { AuthLoaderPage } from "./pages/AuthLoaderPage";
import Presence from "./pages/Presence";
import { Profile } from "./pages/Profile";
import { Settings } from "./pages/Settings";
import { toast } from "sonner";
import LoadingOverlay from "./components/LoadingOverlay";
import { useOnlineStatus } from "./hooks/use-online-status";
import { oidcConfig } from "./services/userManager";
import { withAuthenticationRequired } from "./utils/withAuthenticationRequired";

const queryClient = new QueryClient();

function App() {
  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <Toaster richColors />
        <AuthProvider {...oidcConfig}>
          <BrowserRouter>
            <Suspense fallback={<LoadingOverlay />}>
              <Routes>
                <Route path="/callback" element={<Callback />}></Route>
                <Route path="/*" element={<ProtectedInner />}></Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}
const ProtectedInner = withAuthenticationRequired(Inner, {
  OnRedirecting: () => <AuthLoaderPage />,
});
function Inner() {
  const isOnline = useOnlineStatus();

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = event.data as any;
      console.log("Message received from SW", data);
      if (data?.eventType === "notificationRecieved") {
        toast.info(`${data.data.title}`);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () =>
      navigator.serviceWorker.removeEventListener("message", handler);
  }, []);
  useEffect(() => {
    if (!isOnline) {
      toast.warning(
        "Sei offline. Alcune funzionalità potrebbero non essere disponibili.",
      );
    }
  }, [isOnline]);
  return (
    <Suspense fallback={<LoadingOverlay />}>
      {/* <LoadingOverlay /> */}
      <div className="AppWrapper">
        <TopMenu />
        <div className="Container AppWrapper__content">
          <Routes>
            <Route path="/" element={<Presence />}></Route>
            <Route path="/profile" element={<Profile />}></Route>
            <Route path="/settings" element={<Settings />}></Route>
          </Routes>
        </div>
      </div>
    </Suspense>
  );
}

function Callback() {
  const { isLoading, isAuthenticated, error } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (isLoading || error) {
      return;
    }
    if (isAuthenticated) {
      navigate("/");
      return;
    }
    navigate("/logged-out");
  }, [isLoading, navigate, isAuthenticated, error]);
  if (error) {
    return <div>Errore durante il login: {error.message}</div>;
  }
  return <LoadingOverlay />;
}

export default App;
